/**
 * Диагностические утилиты для аутентификации Supabase
 * Реализация рекомендаций Supabase Support
 */

import { supabase } from '@/integrations/supabase/client';

// Enhanced timeout handling with AbortController
const AUTH_SIGNIN_TIMEOUT = 5000; // 5 seconds for sign in
const AUTH_SIGNUP_TIMEOUT = 15000; // 15 seconds for sign up (trigger handle_new_user может быть медленным)

export const authWithTimeout = async (email: string, password: string) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AUTH_SIGNIN_TIMEOUT);
  
  try {
    const result = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    clearTimeout(timeoutId);
    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Authentication Service Timeout');
    }
    
    throw error;
  }
};

export const signUpWithTimeout = async (email: string, password: string, userData?: Record<string, unknown>) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AUTH_SIGNUP_TIMEOUT);
  
  try {
    const result = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData || {},
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    
    clearTimeout(timeoutId);
    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Registration Service Timeout');
    }
    
    throw error;
  }
};

// Authentication diagnostic function
export const diagnoseAuth = async () => {
  const diagnostics = {
    timestamp: new Date().toISOString(),
    projectRef: 'aoucmkkqfbqccmcerwtr',
    tests: {} as Record<string, unknown>
  };

  try {
    // Test 1: Health Check Endpoint
    console.log('[diagnoseAuth] Testing health endpoint...');
    const healthStart = Date.now();
    
    try {
      const response = await fetch('https://aoucmkkqfbqccmcerwtr.supabase.co/auth/v1/health', {
        signal: AbortSignal.timeout(5000),
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        }
      });
      
      diagnostics.tests.health = {
        status: response.status,
        duration: Date.now() - healthStart,
        success: response.ok
      };
    } catch (error) {
      diagnostics.tests.health = {
        error: error instanceof Error ? error.message : String(error),
        duration: Date.now() - healthStart,
        success: false
      };
    }

    // Test 2: Auth User Retrieval
    console.log('[diagnoseAuth] Testing getUser...');
    const userStart = Date.now();
    
    try {
      const { data, error } = await supabase.auth.getUser();
      diagnostics.tests.getUser = {
        hasUser: !!data.user,
        error: error?.message,
        duration: Date.now() - userStart,
        success: !error
      };
    } catch (error) {
      diagnostics.tests.getUser = {
        error: error instanceof Error ? error.message : String(error),
        duration: Date.now() - userStart,
        success: false
      };
    }

    // Test 3: Session Retrieval
    console.log('[diagnoseAuth] Testing getSession...');
    const sessionStart = Date.now();
    
    try {
      const { data, error } = await supabase.auth.getSession();
      diagnostics.tests.getSession = {
        hasSession: !!data.session,
        error: error?.message,
        duration: Date.now() - sessionStart,
        success: !error
      };
    } catch (error) {
      diagnostics.tests.getSession = {
        error: error instanceof Error ? error.message : String(error),
        duration: Date.now() - sessionStart,
        success: false
      };
    }

  } catch (error) {
    console.error('[diagnoseAuth] Diagnostic failed:', error);
    diagnostics.tests.general_error = {
      error: error instanceof Error ? error.message : String(error),
      success: false
    };
  }

  console.log('[diagnoseAuth] Diagnostics completed:', diagnostics);
  return diagnostics;
};

// Circuit Breaker Pattern Implementation
class AuthCircuitBreaker {
  private failureCount = 0;
  private lastFailureTime = 0;
  private readonly failureThreshold = 3;
  private readonly recoveryTimeout = 30000; // 30 seconds
  private state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';

  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime > this.recoveryTimeout) {
        this.state = 'HALF_OPEN';
        console.log('[CircuitBreaker] Moving to HALF_OPEN state');
      } else {
        throw new Error('Authentication service is temporarily unavailable (Circuit Breaker OPEN)');
      }
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess() {
    this.failureCount = 0;
    this.state = 'CLOSED';
    console.log('[CircuitBreaker] Operation successful, state: CLOSED');
  }

  private onFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.failureThreshold) {
      this.state = 'OPEN';
      console.log('[CircuitBreaker] Failure threshold reached, state: OPEN');
    }
  }

  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime
    };
  }
}

export const authCircuitBreaker = new AuthCircuitBreaker();
